import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../../../services/api/axios";
import {
  BRAND_MODAL_PAPER_PROPS,
  BRAND_MODAL_TITLE_SX,
} from "../../../shared/components/modalStyles";

export default function CompanyImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    return () => abortRef.current?.abort(); // cancel any in-flight request on unmount
  }, []);

  useEffect(() => {
    if (open) return;

    abortRef.current?.abort();
    setFile(null);
    setUploading(false);
    setResult(null);
    setError("");
  }, [open]);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] ?? null);
    setResult(null);
    setError("");
  };

  const handleUpload = async () => {
    if (!file || uploading) return;

    setUploading(true);
    setError("");
    setResult(null);

    const form = new FormData();
    form.append("file", file, file.name);

    // cancel previous if any
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data } = await api.post("/user-companies/import", form, {
        signal: controller.signal,
      });
      setResult(data);
      onDone?.(); // refresh parent table if provided
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }

      // show API-provided detail if present
      const msg =
        err?.response?.data?.detail || err?.message || "Upload failed";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={BRAND_MODAL_PAPER_PROPS}
    >
      <DialogTitle sx={BRAND_MODAL_TITLE_SX}>Import Companies</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Upload a <strong>.csv</strong> or <strong>.xlsx</strong> with your
          companies. Download the template to get the correct headers.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            component={MuiLink}
            href="/static/company_import_template.xlsx"
            target="_blank"
            rel="noopener"
          >
            Download Template
          </Button>

          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
          >
            Choose File
            <input
              hidden
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {file && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected: <strong>{file.name}</strong>
          </Typography>
        )}

        {uploading && <LinearProgress />}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success">Import complete.</Alert>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Imported: {result.imported} • Updated: {result.updated} • Skipped:{" "}
              {result.skipped}
            </Typography>

            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Row Errors
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.errors.map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.error}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
        >
          Upload &amp; Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
